import * as React from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import { selectUserToken } from "../../../../features/userSlice";
import { AdvisorMapsEndpointLogic } from "../../../../logic/DataCollection/Advisor/AdvisorMapsEndPointLogic";
import { advisorCanonicalMetrics } from "../helpers/advisorStaticData";

type AdvisorOption = { id: number; label: string };

interface Props {
  open: boolean;
  onClose: () => void;
  /** called after a rule is created, so the list can be refreshed */
  onCreated: () => void;
}

const AdvisorCreateGapRule = ({ open, onClose, onCreated }: Props) => {
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const destinationTablesData = useSelector(
    (state: any) => state.advisor?.destinationTablesData,
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [ruleTitle, setRuleTitle] = React.useState("");
  const [targetField, setTargetField] = React.useState<string | null>(null);
  // the payload needs the table's id, so the whole option is kept
  const [destinationTable, setDestinationTable] =
    React.useState<AdvisorOption | null>(null);
  const [expressionSql, setExpressionSql] = React.useState("");
  const [expressionJson, setExpressionJson] = React.useState("");
  const [simpleEnglish, setSimpleEnglish] = React.useState("");
  // several metrics can be picked - the payload joins them with commas
  const [dependsOn, setDependsOn] = React.useState<string[]>([]);
  const [dataSource, setDataSource] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);

  const destinationTableOptions: AdvisorOption[] = React.useMemo(
    () =>
      destinationTablesData?.map((item: any) => ({
        label: item?.destination_table,
        id: item?.id,
      })) || [],
    [destinationTablesData],
  );

  // typed by hand but sent as an object, so it has to parse before submitting
  const isExpressionJsonValid = React.useMemo(() => {
    if (!expressionJson.trim()) return true;
    try {
      JSON.parse(expressionJson);
      return true;
    } catch {
      return false;
    }
  }, [expressionJson]);

  const isValidPayload = Boolean(
    ruleTitle.trim() &&
    targetField &&
    destinationTable &&
    expressionSql.trim() &&
    simpleEnglish.trim() &&
    expressionJson.trim() &&
    isExpressionJsonValid,
  );

  // the form is short-lived - reset it so the next open starts clean
  const handleClose = () => {
    setRuleTitle("");
    setTargetField(null);
    setDestinationTable(null);
    setExpressionSql("");
    setExpressionJson("");
    setSimpleEnglish("");
    setDependsOn([]);
    setDataSource("");
    setIsActive(true);
    onClose();
  };

  const handleCreateRule = async () => {
    if (!isValidPayload || !destinationTable) return;
    // optional field - already validated as JSON, so it parses cleanly here
    const expression = expressionJson.trim()
      ? JSON.parse(expressionJson)
      : null;
    setSubmitting(true);
    try {
      const isSuccess = await AdvisorMapsEndpointLogic.createGapRule(
        oauthTokenHeader,
        {
          rule_title: ruleTitle.trim(),
          target_field: targetField,
          destination_table_id: String(destinationTable.id),
          expression_sql: expressionSql.trim(),
          expression,
          description: simpleEnglish.trim(),
          dependency_field: dependsOn.join(",") || null,
          data_source: dataSource.trim() || null,
          is_active: isActive,
        },
      );
      if (isSuccess) {
        onCreated();
        handleClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Create New Rule</DialogTitle>
      <Typography
        variant="body2"
        sx={{ px: 3, pb: 1, fontSize: "14px", color: "text.secondary" }}
      >
        A rule derives one metric on one destination. It is offered to maps only
        while active and matching that metric + destination.
      </Typography>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Rule Title"
              placeholder="How this rule will be called"
              required
              color="secondary"
              value={ruleTitle}
              onChange={(event) => setRuleTitle(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={advisorCanonicalMetrics}
              value={targetField}
              onChange={(_, newValue) => setTargetField(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Field"
                  placeholder="Select a metric from the list"
                  required
                  color="secondary"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={destinationTableOptions}
              value={destinationTable}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => setDestinationTable(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination Table"
                  placeholder="Select a table from the list"
                  required
                  color="secondary"
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Expression (SQL)"
              placeholder="How this rule will be called"
              required
              color="secondary"
              value={expressionSql}
              onChange={(event) => setExpressionSql(event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Expression (JSON op-tree)"
              placeholder='{"op": "*", "args": ["qftd", "cpa_deal"]}'
              helperText={
                isExpressionJsonValid ? "Must be valid JSON" : "Not valid JSON"
              }
              error={!isExpressionJsonValid}
              required
              color="secondary"
              multiline
              rows={2}
              value={expressionJson}
              onChange={(event) => setExpressionJson(event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              sx={{ width: "100%" }}
              label="Simple English"
              placeholder="Filled automatically"
              required
              color="secondary"
              multiline
              rows={2}
              value={simpleEnglish}
              onChange={(event) => setSimpleEnglish(event.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {/* several metrics, picked from the list or typed in */}
            <Autocomplete
              multiple
              freeSolo
              options={advisorCanonicalMetrics}
              value={dependsOn}
              onChange={(_, newValue) => setDependsOn(newValue as string[])}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Depends On"
                  placeholder="Select a metric from the list"
                  color="secondary"
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* free text and optional - sent as null when left blank */}
            <TextField
              sx={{ width: "100%" }}
              label="Data Source"
              placeholder="Select a table from the list"
              color="secondary"
              value={dataSource}
              onChange={(event) => setDataSource(event.target.value)}
            />
          </Grid>
        </Grid>

        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", mt: 2, mb: 1 }}
        >
          Fields marked with * are mandatory.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
          }
          label="Set as active"
        />
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 16px" }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Button color="secondary" disabled={submitting} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!isValidPayload || submitting}
            onClick={handleCreateRule}
            data-tracker="956"
            sx={{ whiteSpace: "nowrap" }}
          >
            Create Rule
            {submitting ? (
              <CircularProgress size={16} color="inherit" sx={{ ml: "8px" }} />
            ) : (
              <AddIcon sx={{ width: "20px", ml: "5px" }} />
            )}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AdvisorCreateGapRule;
